import Phaser from 'phaser';
import {
  CREATOR_BACKGROUNDS,
  CREATOR_BORDERS,
  CREATOR_SONGS,
  DEFAULT_BORDER,
  MAX_DESCRIPTION,
  CREATOR_TEMPLATES,
  CREATOR_TRACKS,
  MAX_NAME,
  applyTemplate,
  cleanDescription,
  findBorder,
  findTemplate,
  findTrack,
  nameError,
  newProject,
  startCashOf,
  stepStartCash,
  templateIsPicture,
  templateSongFor,
  templateTrackFor,
  type CreatorProject,
} from '../../config/creator';
import { HUB_MENU_TRACK } from '../../config/levels';
import { newProjectId } from '../../account/creatorStore';
import { creatorStoreNow } from '../creator/storeAccess';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import {
  CREATOR_ORANGE,
  CREATOR_TEAL,
  CREATOR_TEAL_HEX,
  creatorButton,
  creatorFlash,
  creatorHeader,
} from '../creator/creatorUi';
import { canvasTextArea, canvasTextInput } from '../creator/textInput';

interface Choice {
  template: string;
  song: string;
  track: string;
  background: string;
  showPicture: boolean;
  startCash: number;
  border: string;
  description: string;
  name: string;
}

type Row = 'template' | 'song' | 'track' | 'cash';

const SWATCH = 60;
const SWATCH_GAP = 8;
const GRID_X = 770;
const GRID_Y = 136;
const GRID_COLS = 6;
const BORDER_SWATCH = 40;
/** The selector rows' centres (template, song, track, starting money). */
const ROW_Y = { template: 132, song: 222, track: 312, cash: 402 } as const;

/**
 * Creation Settings (spec 2026-11-01): the first step of a new level, and the
 * editor's gear. Template / Song / Track selectors with ◀ ▶, the background
 * grid on the right, the level name, SAVE bottom-right.
 */
export class CreatorSettingsScene extends Phaser.Scene {
  private editId: string | null = null;
  private existing: CreatorProject | null = null;
  private others: CreatorProject[] = [];
  private choice!: Choice;
  private valueTexts: Record<Row, Phaser.GameObjects.Text> = {} as never;
  private subText?: Phaser.GameObjects.Text;
  private errorText?: Phaser.GameObjects.Text;
  private swatchFrames: Phaser.GameObjects.Rectangle[] = [];
  private pictureBox?: Phaser.GameObjects.Text;
  private borderFrames: Phaser.GameObjects.Rectangle[] = [];
  private descCount?: Phaser.GameObjects.Text;
  private saving = false;

  constructor() {
    super({ key: 'CreatorSettingsScene' });
  }

  init(data: { id?: string }): void {
    this.editId = data?.id ?? null;
    // One-shot data (Phaser re-hands the last start data to a dataless start).
    this.sys.settings.data = {};
  }

  create(): void {
    this.saving = false;
    this.swatchFrames = [];
    this.borderFrames = [];
    drawStarfield(this, 1280, 720);
    creatorHeader(this, this.editId ? 'LEVEL SETTINGS' : 'NEW LEVEL', () => this.leave());
    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());
    void this.loadProjects();
  }

  /** A load problem: say so, then back to the menu (nothing is saved). */
  private bail(message: string): void {
    creatorFlash(this, message, 360, '#ffcf6b');
    this.time.delayedCall(1600, () => this.scene.start('CreatorMenuScene'));
  }

  private leave(): void {
    if (this.editId) this.scene.start('CreatorEditorScene', { id: this.editId });
    else this.scene.start('CreatorMenuScene');
  }

  private async loadProjects(): Promise<void> {
    let all: CreatorProject[];
    try {
      all = await (await creatorStoreNow()).list();
    } catch (e) {
      if (!this.sys.isActive()) return;
      this.bail(e instanceof Error ? e.message : 'Try again');
      return;
    }
    if (!this.sys.isActive()) return;
    this.existing = this.editId ? (all.find((p) => p.id === this.editId) ?? null) : null;
    // Editing a project that can't be found must never quietly make a new one.
    if (this.editId && !this.existing) {
      this.bail("Couldn't find that level");
      return;
    }
    this.others = all;
    const e = this.existing;
    this.choice = e
      ? {
          template: e.template,
          song: e.song,
          track: e.track,
          background: e.background,
          showPicture: e.showPicture,
          startCash: startCashOf(e),
          border: findBorder(e.border).id,
          description: e.description ?? '',
          name: e.name,
        }
      : {
          template: 'blank',
          song: templateSongFor('blank'),
          track: templateTrackFor('blank'),
          background: 'white',
          showPicture: false,
          startCash: startCashOf({}),
          border: DEFAULT_BORDER,
          description: '',
          name: '',
        };
    this.build();
  }

  private build(): void {
    this.selectorRow('template', 'TEMPLATE', ROW_Y.template);
    this.selectorRow('song', 'SONG', ROW_Y.song);
    this.selectorRow('track', 'TRACK', ROW_Y.track);
    this.selectorRow('cash', 'STARTING MONEY', ROW_Y.cash);
    this.buildBackgrounds();
    this.buildBorders();

    this.add
      .text(360, 466, 'LEVEL NAME', { fontSize: '16px', color: '#9aa6ad', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setLetterSpacing(2);
    canvasTextInput(
      this,
      { x: 110, y: 480, w: 500, h: 46 },
      {
        maxLength: MAX_NAME,
        placeholder: 'Name your level',
        testId: 'creator-name',
        value: this.choice.name,
      },
      (v) => {
        this.choice.name = v;
        this.showNameError(false);
      },
    );
    this.errorText = this.add
      .text(360, 540, '', { fontSize: '16px', color: '#ff8a8a', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('creator-name-error');

    // The description (spec 2026-11-03 §3): what the information page shows,
    // after an admin approves it. Emails, phones, links and @names are removed.
    this.add
      .text(110, 566, 'DESCRIPTION', { fontSize: '16px', color: '#9aa6ad', fontStyle: 'bold' })
      .setOrigin(0, 0.5)
      .setLetterSpacing(2);
    this.descCount = this.add
      .text(610, 566, '', { fontSize: '13px', color: '#9aa6ad' })
      .setOrigin(1, 0.5)
      .setName('creator-description-count');
    canvasTextArea(
      this,
      { x: 110, y: 580, w: 500, h: 110 },
      {
        maxLength: MAX_DESCRIPTION,
        placeholder: 'Tell players about your level (optional)',
        testId: 'creator-description',
        value: this.choice.description,
      },
      (v) => {
        this.choice.description = v;
        this.refreshDescCount();
      },
    );
    this.refreshDescCount();

    creatorButton(
      this,
      1160,
      676,
      'SAVE',
      { name: 'creator-settings-save', fill: CREATOR_ORANGE, width: 180, height: 56, fontSize: 28 },
      () => void this.save(),
    );
    this.refresh();
  }

  /** One ◀ value ▶ row. */
  private selectorRow(kind: Row, label: string, y: number): void {
    this.add
      .text(360, y - 42, label, { fontSize: '16px', color: '#9aa6ad', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setLetterSpacing(2);
    this.add.rectangle(360, y, 400, 60, 0x15131f, 1).setStrokeStyle(2, CREATOR_TEAL);
    this.valueTexts[kind] = this.add
      .text(360, kind === 'template' ? y - 8 : y, '', {
        fontSize: '28px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName(`creator-${kind}-value`);
    if (kind === 'template') {
      this.subText = this.add
        .text(360, y + 20, '', { fontSize: '14px', color: '#9aa6ad' })
        .setOrigin(0.5)
        .setName('creator-template-name');
    }
    for (const [dx, dir, glyph] of [
      [-240, -1, '◀'],
      [240, 1, '▶'],
    ] as const) {
      creatorButton(
        this,
        360 + dx,
        y,
        glyph,
        {
          name: `creator-${kind}-${dir < 0 ? 'prev' : 'next'}`,
          width: 60,
          height: 60,
          fontSize: 28,
        },
        () => this.step(kind, dir),
      );
    }
  }

  private step(kind: Row, dir: 1 | -1): void {
    if (kind === 'cash') {
      this.choice.startCash = stepStartCash(this.choice.startCash, dir);
      this.refresh();
      return;
    }
    const lists = {
      template: CREATOR_TEMPLATES.map((t) => t.id),
      song: CREATOR_SONGS.map((s) => s.id),
      track: CREATOR_TRACKS.map((t) => t.id),
    };
    const ids = lists[kind];
    const i = ids.indexOf(this.choice[kind]);
    const next = ids[(i + dir + ids.length) % ids.length];
    this.choice[kind] = next;
    if (kind === 'template') {
      // A template brings its own song, track and picture.
      this.choice.song = templateSongFor(next);
      this.choice.track = templateTrackFor(next);
      // Grassland, Empty Beach and Hills are nothing BUT their picture.
      this.choice.showPicture = findTemplate(next).levelId !== null || templateIsPicture(next);
      if (next === 'blank') this.choice.background = 'white';
    }
    this.refresh();
  }

  private buildBackgrounds(): void {
    this.add
      .text(GRID_X + (GRID_COLS * (SWATCH + SWATCH_GAP)) / 2 - SWATCH_GAP / 2, 108, 'BACKGROUND', {
        fontSize: '16px',
        color: '#9aa6ad',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(2);
    CREATOR_BACKGROUNDS.forEach((b, i) => {
      const x = GRID_X + (i % GRID_COLS) * (SWATCH + SWATCH_GAP) + SWATCH / 2;
      const y = GRID_Y + Math.floor(i / GRID_COLS) * (SWATCH + SWATCH_GAP) + SWATCH / 2;
      if (b.key && this.textures.exists(b.key)) {
        this.add.image(x, y, b.key).setDisplaySize(SWATCH, SWATCH);
      } else {
        this.add.rectangle(x, y, SWATCH, SWATCH, b.color ?? 0x444444);
      }
      if (b.key) {
        this.add
          .text(x, y + SWATCH / 2 - 9, b.label, {
            fontSize: '11px',
            color: '#ffffff',
            fontStyle: 'bold',
            backgroundColor: '#000000aa',
          })
          .setOrigin(0.5);
      }
      const frame = this.add
        .rectangle(x, y, SWATCH, SWATCH)
        .setStrokeStyle(2, 0x000000, 0.6)
        .setInteractive({ useHandCursor: true })
        .setName(`creator-bg-${b.id}`);
      frame.setData('id', b.id);
      frame.on('pointerup', () => {
        this.choice.background = b.id;
        this.refresh();
      });
      this.swatchFrames.push(frame);
    });
    const rows = Math.ceil(CREATOR_BACKGROUNDS.length / GRID_COLS);
    const boxY = GRID_Y + rows * (SWATCH + SWATCH_GAP) + 16;
    this.pictureBox = this.add
      .text(GRID_X, boxY, '', { fontSize: '20px', color: '#e8f4f1', fontStyle: 'bold' })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true })
      .setName('creator-show-picture');
    this.pictureBox.on('pointerup', () => {
      // Only a level template's picture can be hidden.
      if (findTemplate(this.choice.template).levelId === null) return;
      this.choice.showPicture = !this.choice.showPicture;
      this.refresh();
    });
  }

  /** BORDER COLOUR: the level's row in Explore and in a chapter (spec 2026-11-03). */
  private buildBorders(): void {
    const top = GRID_Y + Math.ceil(CREATOR_BACKGROUNDS.length / GRID_COLS) * (SWATCH + SWATCH_GAP);
    this.add
      .text(GRID_X, top + 56, 'BORDER COLOUR', {
        fontSize: '16px',
        color: '#9aa6ad',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(2);
    CREATOR_BORDERS.forEach((b, i) => {
      const x = GRID_X + BORDER_SWATCH / 2 + (i % 6) * (BORDER_SWATCH + 12);
      const y = top + 96 + Math.floor(i / 6) * (BORDER_SWATCH + 10);
      this.add.rectangle(x, y, BORDER_SWATCH, BORDER_SWATCH, b.color, 1);
      const frame = this.add
        .rectangle(x, y, BORDER_SWATCH, BORDER_SWATCH)
        .setStrokeStyle(2, 0x000000, 0.6)
        .setInteractive({ useHandCursor: true })
        .setName(`creator-border-${b.id}`);
      frame.setData('id', b.id);
      frame.on('pointerup', () => {
        this.choice.border = b.id;
        this.refresh();
      });
      this.borderFrames.push(frame);
    });
  }

  private refreshDescCount(): void {
    this.descCount?.setText(`${this.choice.description.length} / ${MAX_DESCRIPTION}`);
  }

  private refresh(): void {
    const t = findTemplate(this.choice.template);
    this.valueTexts.template.setText(t.label);
    this.subText?.setText(
      t.levelId === null && !t.picture ? 'A white map, no decorations' : t.name,
    );
    this.valueTexts.song.setText(
      CREATOR_SONGS.find((s) => s.id === this.choice.song)?.label ?? 'None',
    );
    this.valueTexts.track.setText(findTrack(this.choice.track).label);
    this.valueTexts.cash.setText(`$${this.choice.startCash.toLocaleString('en-US')}`);
    for (const f of this.swatchFrames) {
      const on = f.getData('id') === this.choice.background;
      f.setStrokeStyle(on ? 5 : 2, on ? 0xffffff : 0x000000, on ? 1 : 0.6);
      f.setDepth(on ? 2 : 1);
    }
    for (const f of this.borderFrames) {
      const on = f.getData('id') === this.choice.border;
      f.setStrokeStyle(on ? 5 : 2, on ? 0xffffff : 0x000000, on ? 1 : 0.6);
      f.setDepth(on ? 2 : 1);
    }
    const hasPicture = t.levelId !== null;
    const shown = this.choice.showPicture || templateIsPicture(t.id);
    this.pictureBox
      ?.setText(`${shown ? '☑' : '☐'}  Show template picture`)
      .setColor(hasPicture ? '#e8f4f1' : '#5c6168');
  }

  private showNameError(show: boolean): string | null {
    const err = nameError(this.choice.name, this.others, this.editId ?? undefined);
    this.errorText?.setText(show && err ? err : '');
    return err;
  }

  private async save(): Promise<void> {
    if (this.saving) return;
    const err = this.showNameError(true);
    if (err) return;
    this.saving = true;
    const c = this.choice;
    let project: CreatorProject;
    if (this.existing) {
      let p: CreatorProject = {
        ...this.existing,
        name: c.name.trim(),
        song: c.song,
        startCash: c.startCash,
        border: c.border,
      };
      const description = cleanDescription(c.description);
      if (description) p.description = description;
      else delete p.description;
      // The new track first: a Blank template lays its plots along it.
      if (p.template !== c.template) p = applyTemplate(p, c.template, c.track);
      p = { ...p, track: c.track, background: c.background, showPicture: c.showPicture };
      project = p;
    } else {
      project = newProject({
        id: newProjectId(),
        name: c.name,
        template: c.template,
        song: c.song,
        track: c.track,
        background: c.background,
        showPicture: c.showPicture,
        startCash: c.startCash,
        border: c.border,
        description: c.description,
        now: Date.now(),
      });
    }
    try {
      const saved = await (await creatorStoreNow()).save(project);
      if (!this.sys.isActive()) return;
      this.scene.start('CreatorEditorScene', { id: saved.id });
    } catch (e) {
      this.saving = false;
      if (!this.sys.isActive()) return;
      const msg = e instanceof Error ? e.message : 'Could not save';
      this.errorText?.setText(msg);
      creatorFlash(this, msg, 360, CREATOR_TEAL_HEX);
    }
  }
}
